import setOrderFileUpload from "@hardware/Store/Handlers/Order/CustomOrderFileUpload";
import { CustomActions, CustomState } from "@hardware/Store/Reducers";
import { colorStrings } from "@hardware/Utilities/variables";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setIsUploading from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetIsUploading";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import XCircle from "@insite/mobius/Icons/XCircle";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const { fulfillmentMethod, pickUpWarehouse } = state.context.session;

    return {
        cartId,
        cart,
        shipToState: getShipToState(state, cart?.shipToId),
        billToState: getBillToState(state, cart?.billToId),
        shippingPageNavLink: getPageLinkByPageType(state, "CheckoutShippingPage"),
        file: state.orderFileUpload.file,
    };
};

const mapDispatchToProps = {
    loadShipTo,
    loadBillTo,
    // Verndale Customization: Added setOrderFileUpload to the props
    setOrderFileUpload,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & WidgetProps & HasHistory;

export interface CheckoutReviewAndSubmitFileUploadStyles {
    fileUploaderGridContainer?: GridContainerProps;
    fileUploaderGridItem?: GridItemProps;
    warningSection?: InjectableCss;
    warningText?: InjectableCss;
    warningIcon?: IconPresentationProps;
    removeCartLineIcon?: IconPresentationProps;
}

export const shippingInfoStyles: CheckoutReviewAndSubmitFileUploadStyles = {
    removeCartLineIcon: { src: XCircle },
    fileUploaderGridItem: {
        width: 12,
        css: css`
            flex-direction: column;

            input[type="file"] {
                display: none;
            }

            .order-file-upload {
                min-width: 100px;
                height: 40px;
                color: ${getColor(colorStrings.commonGlobalWhite)};
                background-color: #00656b;

                cursor: pointer;
                font-family: inherit;
                min-height: 40px;
                backface-visibility: hidden;
                transition: all 200ms ease-in-out;
                word-break: keep-all;
                padding: 0 21px;
                transform: perspective(1px);
                line-height: 40px;
                font-size: 15px;
                font-weight: 700;

                &:hover {
                    background: hsl(195.5, 83.8%, 15.2%);
                    border-color: hsl(195.5, 83.8%, 15.2%);
                }
            }

            .file-list {
                display: flex;
                flex-direction: column;
                gap: 10px;
            }

            .file-name-item {
                height: 40px;
                color: ${getColor(colorStrings.commonGlobalWhite)};
                background-color: ${getColor(colorStrings.secondaryMain)};
                min-width: 100px;
                padding: 10px;
                width: 100%;
                display: flex;
                justify-content: space-between;
                gap: 10px;
                font-size: 14px;
                font-weight: 700;
            }
        `,
    },
};

const StyledSection = getStyledWrapper("section");
const styles = shippingInfoStyles;

const CheckoutReviewAndSubmitFileUpload = ({
    billToState,
    setOrderFileUpload,
}: Props) => {
    // Verndale Customization
    const fileUploadRef = useRef({ value: "" } as HTMLInputElement);
    const [file, setFile] = useState<File | null>(null);
    const [errorLabel, setErrorLabel] = useState<string | undefined>(undefined);

    // Verndale Customization
    const fileChangeHandler = (event: React.ChangeEvent<any>) => { 
        // Update the state
        setErrorLabel(undefined);
        setIsUploading({ isUploading: false });
        const uploadedFile = event.target.files && event.target.files[0];
        setFile(uploadedFile);

        if (uploadedFile) {
            // show error message if file extension is not pdf or file size is greater than 1MB
            const fileExtension = getFileExtension(uploadedFile.name);
            const uploadLimitExceeded = uploadedFile.size > 1048576;
            const incorrectFileExtension = ["pdf"].indexOf(fileExtension) === -1;

            if (incorrectFileExtension) {
                setErrorLabel(translate("Incorrect file extension. Supported Files: .pdf"));
                removeFile(uploadedFile);
                return;
            }

            if (uploadLimitExceeded) {
                setErrorLabel(translate("Custom_Order_LimitExceeded").replace("{0}", uploadedFile.name));
                removeFile(uploadedFile);
                return;
            }

            setOrderFileUpload(uploadedFile);
           
        }
    };

    const removeFile = (file: any) => {
        setFile(null);
    };

    return (
        <>
            {/* Verndale Customization: Added this block to show Quote add input file */}
            <GridContainer {...styles.fileUploaderGridContainer}>
                <GridItem {...styles.fileUploaderGridItem}>
                    <Typography variant="h6" style={{ margin: "0 0 5px" }}>
                        {translate("File Order Upload")}
                    </Typography>
                    <Typography variant="p" id="description">
                        {translate("Custom_Order_UploadHint")}
                    </Typography>
                    <label className="order-file-upload" htmlFor={`order-file-upload-${billToState.id}`}>
                        {translate("Add a File")}
                    </label>
                    <input
                        ref={fileUploadRef}
                        onChange={fileChangeHandler}
                        type="file"
                        id={`order-file-upload-${billToState.id}`}
                        name="files"
                        onClick={() => setErrorLabel(undefined)}
                        multiple
                    />

                    {errorLabel && (
                        <StyledSection {...styles.warningSection}>
                            <Icon {...styles.warningIcon} />
                            <Typography {...styles.warningText} data-test-selector="warning_bad_file">
                                {errorLabel}
                            </Typography>
                        </StyledSection>
                    )}
                </GridItem>
                {file && (
                    <GridItem {...styles.fileUploaderGridItem}>
                        <ul className="file-list">
                            <li key={file.name}>
                                <Clickable
                                    data-test-selector="fileName"
                                    onClick={() => removeFile(file)}
                                    className="file-name-item"
                                >
                                    <span>{file.name}</span>
                                    <Icon color="white" src={XCircle} onClick={() => removeFile(file)} />
                                </Clickable>
                            </li>
                        </ul>
                    </GridItem>
                )}
            </GridContainer>

            {/* END Verndale Customization */}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutReviewAndSubmitFileUpload)),
    definition: {
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
        displayName: "Files",
    },
};

export default widgetModule;
